/**
 * The shell's navigation, in one place because three things render it now: the
 * sidebar (full list, grouped, with sub-navs), the mobile dock (a handful of
 * icons) and the phone's account sheet (the groups the dock does not carry).
 * The top bar's search reads it too, so a route added here is findable without
 * being listed anywhere else. Keeping one source means none of them can drift
 * from the sidebar.
 */

/**
 * The setup and support pages, kept as a named group because two places need
 * the list itself and not just its place in the sidebar: the sidebar draws it
 * under its own heading, and the phone's account sheet carries it as a section
 * (see AccountSheet.jsx).
 *
 * `dock: false` keeps it out of the mobile dock. The dock is glyph-only and
 * already scrolls; these are looked for by name rather than recognised by
 * shape, so on a phone they belong in the account sheet — which is the one
 * place a trader reads navigation as words.
 */
const GET_STARTED_GROUP = {
  label: 'Get started',
  dock: false,
  items: [
    { to: '/account-setup', label: 'Account setup', icon: 'nav-setup' },
    { to: '/downloads', label: 'Downloads', icon: 'nav-downloads' },
    // N77 — the live dashboard's Get Started videos.
    // { to: '/tutorials', label: 'Tutorials', icon: 'nav-tutorials' },
    { to: '/help-centre', label: 'Help centre', icon: 'nav-help' },
  ],
};

/** Just the rows, for the account sheet — it renders its own heading. */
export const GET_STARTED = GET_STARTED_GROUP.items;

export const GROUPS = [
  {
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: 'nav-dashboard' },
      { to: '/account-metrics', label: 'Account Metrics', icon: 'nav-metrics' },
      { to: '/trade-history', label: 'Trade History', icon: 'nav-trades' },
      { to: '/trading-assessment', label: 'Purchase Assessment', icon: 'nav-assessment' },
      { to: '/payment-history', label: 'Payment History', icon: 'nav-payments' },
      // `section`: the subtree's nav is its own aside (VerificationAside), as
      // with Affiliate, so the row has no sub-list and stays lit under it.
      { to: '/verification', label: 'Payment & Verification', icon: 'nav-verification', section: true, gate: 'verify' },
      {
        to: '/payout',
        label: 'Payout & Certificates',
        icon: 'nav-payout',
        items: [
          { to: '/payout', label: 'Payout' },
          { to: '/payout/certificates', label: 'Certificates' },
        ],
      },
      { to: '/competition', label: 'Competition', icon: 'nav-competition', section: true },
      // Full-screen 3D teasers outside the shell, so leaving the sidebar is
      // expected. `badge` marks them as not launched yet.
      { to: '/alpha-dna', label: 'Alpha DNA', icon: 'nav-alphadna', badge: 'Soon' },
      { to: '/alpha-ops', label: 'Alpha OPS', icon: 'nav-alphaops', badge: 'Soon' },
      // N02 — the live dashboard's Coupons page, and so findable from the top
      // bar's search as "coupon".
      { to: '/coupons', label: 'Coupons', icon: 'nav-coupons' },
      // N75 — the live dashboard's market tools. `keywords` because traders
      // look for the tools inside it by name, not for "Trademate".
      {
        to: '/trademate',
        label: 'Trademate',
        icon: 'nav-trademate',
        keywords: ['Market screener', 'Economic calendar', 'Market news', 'Trade journal', 'Market report', 'TradingView chart'],
      },
      { to: '/affiliate', label: 'Affiliate', icon: 'nav-affiliate', section: true },
      { to: '/my-alpha', label: 'My Alpha', icon: 'nav-myalpha' },
      // N80 — cTrader Web inside the dashboard, for cTrader clients only:
      // `gate` hides the row unless `/is-ctrader/user/` says so (useNavGates).
      { to: '/ctrader', label: 'Launch cTrader', icon: 'nav-ctrader', gate: 'ctrader' },
    ],
  },
  GET_STARTED_GROUP,
];

/**
 * What the dock carries: everything the sidebar does, in the same order, so a
 * route added above turns up in both without a second list to remember.
 *
 * A sub-nav is flattened to its parent's own route — there is no room to expand
 * a group inside a 44px circle, and every group's index route is a real page.
 * More icons than fit is expected: the dock scrolls (see Dock.jsx).
 *
 * A group can opt out with `dock: false` when its rows read as words rather
 * than glyphs — those surface in the account sheet instead. That is a property
 * of the group rather than a second list here, so the two can't drift.
 */
export const DOCK = GROUPS.filter((group) => group.dock !== false)
  .flatMap((group) => group.items)
  .map(({ to, label, icon, gate }) => ({ to, label, icon, gate }));
